/**
 * OfferDetail — a single offer's page.
 *
 *   • slug 'ai-lab'  → AiLabDetail: rich, hand-crafted page (content from the
 *     AI Lab landing) — directions, partner earnings per tier, student results.
 *   • any other slug → GenericDetail: API-driven offer (terms + creatives).
 *
 * Both share a sticky bottom CTA to select the offer.
 */
import { Link, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowLeft, ArrowUpRight, BadgeCheck, Check, Coins, Globe, Image as ImageIcon, Layers, Percent, Send } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  Skeleton,
} from '@/components/ui';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents } from '@/lib/format';
import type { Offer } from '@/lib/types';

// ─────────────────────────────────────────────────────────────────────────
//  AI Lab — built-in offer content (from the AI Lab landing)
// ─────────────────────────────────────────────────────────────────────────

const AI_LAB = {
  name: 'AI Lab',
  tagline: 'Академия AI-монетизации',
  revshare: 50,
  pitch:
    'Обучение системе: трафик → аудитория → воронка → деньги. Работает в любой нише — без опыта и без рекламного бюджета. Первые результаты ученики получают за 2–4 недели.',
  directions: ['Telegram', 'YouTube', 'AI OFM', 'Арбитраж'],
  stats: [
    { value: '500+', label: 'учеников прошли' },
    { value: '100+', label: 'часов уроков' },
    { value: '2–4 нед.', label: 'до первых денег' },
    { value: '40+', label: 'готовых связок' },
  ],
  tracks: [
    { tag: 'Telegram', flow: 'Канал → Трафик → Продажи', modules: '12 модулей',
      desc: 'УБТ и платный трафик, воронки, партнёрки — полная система запуска Telegram-проекта.' },
    { tag: 'YouTube', flow: 'Видео → Рекомендации → Доход', modules: '14 модулей',
      desc: 'Превью, сценарии, монтаж. Цикл: идея → съёмка → публикация → аналитика → монетизация.' },
    { tag: 'AI OFM', flow: 'Модели → Трафик → Доход', modules: '20 уроков',
      desc: 'Reels, TikTok, adult-трафик. Заработок на AI-моделях: от генерации до монетизации.' },
    { tag: 'Арбитраж', flow: 'Reels / YouTube / SEO → Масштаб', modules: '11 модулей',
      desc: 'Экосистема трафика: связки, нейрокомментинг и масштабирование через команду.' },
  ],
  tiers: [
    { name: 'Месячный', priceCents: 4900, period: '/мес', hot: false,
      blurb: 'Полный доступ к Академии, поддержка кураторов, живые эфиры, 2 бота на месяц.' },
    { name: 'Годовой', priceCents: 29000, period: '/год', hot: true,
      blurb: 'Всё из месячного + все боты на год, созвон на старте и личная дорожная карта.' },
    { name: 'Под ключ', priceCents: 146000, period: '/год', hot: false,
      blurb: '3 персональных созвона, сопровождение по дорожной карте, доведение до результата.' },
  ],
  cases: [
    { amount: '$12 000', period: 'за 4 месяца', name: 'Артём К.', niche: 'Гемблинг · Reels + Shorts',
      text: 'Освоил систему вертикального контента и перелил трафик. При вложениях $150 — $12 000 за 4 месяца.' },
    { amount: '10 000', period: 'заявок / мес', name: 'Миша Р.', niche: 'Дейтинг · Нейрокомментинг',
      text: 'Настроил систему трафика через нейрокомментинг — 10 000 заявок в канал за первый месяц.' },
    { amount: '$6 500', period: 'за 2 месяца', name: 'Влад П.', niche: 'Инфобиз · YouTube + Telegram',
      text: 'Построил систему продаж через YouTube и Telegram. Воронка дала $6 500 за два месяца.' },
  ],
};

/** AI Lab marketing landing (served by the backend). Swap for the prod domain. */
const AI_LAB_SITE_URL = 'http://localhost:3000/landing/ai';

// ─────────────────────────────────────────────────────────────────────────
//  Crypto Trading — built-in offer content (Telegram-бот @temaalt_crypto_bot)
// ─────────────────────────────────────────────────────────────────────────

const CRYPTO = {
  name: 'Crypto Trading — Tema Alt',
  tagline: 'Индивидуальная торговля в Telegram',
  revshare: 50,
  pitch:
    'Telegram-бот Tema Alt (@temaalt_crypto_bot) набирает людей на индивидуальную торговлю с командой. Лид переходит по вашей ссылке и подключается к торговле. Профит, который платформа получает с торговли лида, делится 50/50 — половина начисляется вам.',
  tags: ['Telegram', 'Индивидуальная торговля', 'Крипто'],
  stats: [
    { value: '50%', label: 'ваша доля профита' },
    { value: 'Telegram', label: 'точка входа' },
    { value: '50/50', label: 'сплит профита с лида' },
    { value: 'Авто', label: 'начисление после профита' },
  ],
  steps: [
    { n: '01', title: 'Переход по ссылке', desc: 'Пользователь кликает вашу партнёрскую ссылку /r/… — клик и ref-код закрепляются за вами.' },
    { n: '02', title: 'Бот Tema Alt', desc: 'Лид попадает в Telegram-бота и знакомится с форматом индивидуальной торговли.' },
    { n: '03', title: 'Набор на торговлю', desc: 'Лид подключается к индивидуальной торговле с командой Tema Alt.' },
    { n: '04', title: 'Профит → сплит 50/50', desc: 'Профит с торговли лида приходит платформе и делится 50/50 — половина начисляется вам.' },
  ],
  income: [
    { profit: 30000, label: 'Небольшой профит' },
    { profit: 120000, label: 'Средний профит', hot: true },
    { profit: 400000, label: 'Крупный профит' },
  ],
  terms: [
    { value: 'Сплит 50/50', label: 'Модель', desc: 'Профит платформы с торговли лида делится 50/50 между платформой и партнёром. Фиксированного CPA нет.' },
    { value: 'По ref-коду', label: 'Атрибуция', desc: 'Засчитывается лид, пришедший по вашему клику с вашим кодом в ссылке.' },
    { value: 'С профита', label: 'Начисление', desc: 'Ваши 50% зачисляются на баланс с подтверждённого профита по лиду.' },
    { value: 'Честный источник', label: 'Трафик', desc: 'Соблюдайте правила площадок — без обмана и поддельных обещаний доходности.' },
  ],
};

/** Crypto offer landing (served by the backend). Swap for the prod domain. */
const CRYPTO_SITE_URL = 'http://localhost:3000/landing/crypto';

// ─────────────────────────────────────────────────────────────────────────
//  Shared bits
// ─────────────────────────────────────────────────────────────────────────

function SectionHead({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
        {eyebrow}
      </div>
      <h2 className="mt-1 text-[20px] font-semibold tracking-tight text-[var(--text)]">{title}</h2>
    </div>
  );
}

function BackLink() {
  return (
    <Link to={'/offers' as never} className="inline-block mb-3">
      <Button variant="ghost" size="sm">
        <ArrowLeft size={14} /> К офферам
      </Button>
    </Link>
  );
}

const MODEL_META: Record<Offer['payoutModel'], { label: string; color: string; Icon: LucideIcon }> = {
  revshare: { label: 'RevShare', color: 'var(--success)',       Icon: Percent },
  cpa:      { label: 'CPA',      color: 'var(--accent-bright)', Icon: Coins },
  hybrid:   { label: 'Hybrid',   color: 'var(--warning)',       Icon: Layers },
};

// ─────────────────────────────────────────────────────────────────────────
//  AI Lab detail
// ─────────────────────────────────────────────────────────────────────────

function AiLabDetail({ selected, onSelect, pending }: { selected: boolean; onSelect: () => void; pending: boolean }) {
  const GREEN = 'var(--success)';

  return (
    <div className="mx-auto max-w-[1040px] pb-28">
      <BackLink />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <Card className="overflow-hidden" style={{ borderColor: 'color-mix(in oklch, var(--success) 24%, var(--border))' }}>
        <div
          className="p-7 md:p-9"
          style={{
            background:
              'radial-gradient(130% 150% at 0% 0%, color-mix(in oklch, var(--success) 15%, var(--bg-raised)) 0%, var(--bg-raised) 60%)',
          }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[var(--tracking-eyebrow)]"
              style={{
                backgroundColor: 'color-mix(in oklch, var(--success) 15%, transparent)',
                color: GREEN,
                border: '1px solid color-mix(in oklch, var(--success) 30%, transparent)',
              }}
            >
              <Percent size={11} strokeWidth={2.5} /> RevShare
            </span>
            {selected ? (
              <Badge tone="accent"><BadgeCheck size={11} strokeWidth={2.5} /> Выбран</Badge>
            ) : (
              <Badge tone="success" dot>Активен</Badge>
            )}
          </div>

          <h1 className="mt-4 text-[34px] md:text-[44px] font-bold leading-[1.04] tracking-tight text-[var(--text)]">
            {AI_LAB.name}
          </h1>
          <p className="mt-1.5 text-[15px] font-medium" style={{ color: GREEN }}>
            {AI_LAB.tagline}
          </p>
          <p className="mt-4 max-w-[620px] text-[14px] leading-relaxed text-[var(--text-secondary)]">
            {AI_LAB.pitch}
          </p>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {AI_LAB.directions.map((d) => (
              <span
                key={d}
                className="rounded-[var(--radius-xs)] border border-[var(--border)] bg-[color-mix(in_oklch,var(--bg-base)_55%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-muted)]"
              >
                {d}
              </span>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {AI_LAB.stats.map((s) => (
              <div key={s.label}>
                <div
                  className="text-[22px] font-bold tabular-nums leading-none text-[var(--text)]"
                  style={{ letterSpacing: 'var(--tracking-tight)' }}
                >
                  {s.value}
                </div>
                <div className="mt-1 text-[11px] text-[var(--text-quiet)]">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-[var(--border-subtle)] flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                Твоя выплата
              </div>
              <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                <span
                  className="text-[40px] font-bold leading-none tabular-nums"
                  style={{ color: GREEN, letterSpacing: 'var(--tracking-tight)' }}
                >
                  {AI_LAB.revshare}%
                </span>
                <span className="text-[14px] font-medium text-[var(--text-muted)]">
                  RevShare с каждой подтверждённой продажи
                </span>
              </div>
            </div>
            <a
              href={AI_LAB_SITE_URL}
              target="_blank"
              rel="noreferrer"
              className="group shrink-0"
            >
              <Button variant="secondary" size="sm">
                <Globe size={14} strokeWidth={2} />
                Сайт оффера
                <ArrowUpRight
                  size={14}
                  strokeWidth={2.5}
                  className="transition-transform duration-[var(--duration-base)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </Button>
            </a>
          </div>
        </div>
      </Card>

      {/* ── Directions ───────────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Что внутри" title="4 направления академии" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {AI_LAB.tracks.map((t) => (
            <Card key={t.tag} className="flex flex-col p-5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[14px] font-semibold text-[var(--text)]">{t.tag}</span>
                <span className="text-[11px] tabular-nums text-[var(--text-quiet)]">{t.modules}</span>
              </div>
              <div className="mt-1.5 text-[12px] font-medium" style={{ color: GREEN }}>
                {t.flow}
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-[var(--text-muted)]">{t.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Earnings per tier ────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Сколько ты зарабатываешь" title="Доход с каждого тарифа" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {AI_LAB.tiers.map((t) => (
            <Card
              key={t.name}
              className={'flex flex-col p-5 ' + (t.hot ? 'ring-1 ring-[var(--success)]' : '')}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                  {t.name}
                </span>
                {t.hot && <Badge tone="success">Популярный</Badge>}
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-[11px] text-[var(--text-quiet)]">клиент платит</span>
                <span className="text-[15px] font-semibold tabular-nums text-[var(--text-secondary)]">
                  {formatCents(t.priceCents)}
                </span>
                <span className="text-[11px] text-[var(--text-quiet)]">{t.period}</span>
              </div>

              <div className="mt-3 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--bg-base)]/40 px-4 py-3">
                <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                  Твой доход · 50%
                </div>
                <div
                  className="mt-1 text-[28px] font-bold tabular-nums leading-none"
                  style={{ color: GREEN, letterSpacing: 'var(--tracking-tight)' }}
                >
                  {formatCents(Math.round(t.priceCents * 0.5))}
                </div>
              </div>

              <p className="mt-3 text-[12px] leading-relaxed text-[var(--text-muted)]">{t.blurb}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Student results ──────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Социальное доказательство" title="Результаты учеников" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {AI_LAB.cases.map((c) => (
            <Card key={c.name} className="flex flex-col p-5">
              <div className="flex items-baseline gap-1.5">
                <span
                  className="text-[24px] font-bold tabular-nums leading-none"
                  style={{ color: GREEN, letterSpacing: 'var(--tracking-tight)' }}
                >
                  {c.amount}
                </span>
                <span className="text-[12px] text-[var(--text-quiet)]">{c.period}</span>
              </div>
              <p className="mt-2.5 flex-1 text-[13px] leading-relaxed text-[var(--text-secondary)]">
                {c.text}
              </p>
              <div className="mt-3 pt-3 border-t border-[var(--border-subtle)] flex items-center gap-2.5">
                <span
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-semibold shrink-0"
                  style={{
                    backgroundColor: 'color-mix(in oklch, var(--success) 16%, transparent)',
                    color: GREEN,
                  }}
                  aria-hidden
                >
                  {c.name[0]}
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-[var(--text)] truncate">{c.name}</div>
                  <div className="text-[11px] text-[var(--text-quiet)] truncate">{c.niche}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <StickyCta label={AI_LAB.name} selected={selected} onSelect={onSelect} pending={pending} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Crypto Trading detail
// ─────────────────────────────────────────────────────────────────────────

function CryptoDetail({
  selected,
  onSelect,
  pending,
}: {
  selected: boolean;
  onSelect: () => void;
  pending: boolean;
}) {
  const GOLD = 'var(--warning)';

  return (
    <div className="mx-auto max-w-[1040px] pb-28">
      <BackLink />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <Card className="overflow-hidden" style={{ borderColor: 'color-mix(in oklch, var(--warning) 24%, var(--border))' }}>
        <div
          className="p-7 md:p-9"
          style={{
            background:
              'radial-gradient(130% 150% at 0% 0%, color-mix(in oklch, var(--warning) 15%, var(--bg-raised)) 0%, var(--bg-raised) 60%)',
          }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[var(--tracking-eyebrow)]"
              style={{
                backgroundColor: 'color-mix(in oklch, var(--warning) 15%, transparent)',
                color: GOLD,
                border: '1px solid color-mix(in oklch, var(--warning) 30%, transparent)',
              }}
            >
              <Percent size={11} strokeWidth={2.5} /> RevShare
            </span>
            {selected ? (
              <Badge tone="accent"><BadgeCheck size={11} strokeWidth={2.5} /> Выбран</Badge>
            ) : (
              <Badge tone="success" dot>Активен</Badge>
            )}
          </div>

          <h1 className="mt-4 text-[34px] md:text-[44px] font-bold leading-[1.04] tracking-tight text-[var(--text)]">
            {CRYPTO.name}
          </h1>
          <p className="mt-1.5 text-[15px] font-medium" style={{ color: GOLD }}>
            {CRYPTO.tagline}
          </p>
          <p className="mt-4 max-w-[640px] text-[14px] leading-relaxed text-[var(--text-secondary)]">
            {CRYPTO.pitch}
          </p>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {CRYPTO.tags.map((d) => (
              <span
                key={d}
                className="rounded-[var(--radius-xs)] border border-[var(--border)] bg-[color-mix(in_oklch,var(--bg-base)_55%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-muted)]"
              >
                {d}
              </span>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {CRYPTO.stats.map((s) => (
              <div key={s.label}>
                <div
                  className="text-[22px] font-bold tabular-nums leading-none text-[var(--text)]"
                  style={{ letterSpacing: 'var(--tracking-tight)' }}
                >
                  {s.value}
                </div>
                <div className="mt-1 text-[11px] text-[var(--text-quiet)]">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-[var(--border-subtle)] flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                Твоя выплата
              </div>
              <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                <span
                  className="text-[40px] font-bold leading-none tabular-nums"
                  style={{ color: GOLD, letterSpacing: 'var(--tracking-tight)' }}
                >
                  {CRYPTO.revshare}%
                </span>
                <span className="text-[14px] font-medium text-[var(--text-muted)]">
                  ваша доля сплита профита по лиду
                </span>
              </div>
            </div>
            <a href={CRYPTO_SITE_URL} target="_blank" rel="noreferrer" className="group shrink-0">
              <Button variant="secondary" size="sm">
                <Send size={14} strokeWidth={2} />
                Сайт оффера
                <ArrowUpRight
                  size={14}
                  strokeWidth={2.5}
                  className="transition-transform duration-[var(--duration-base)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </Button>
            </a>
          </div>
        </div>
      </Card>

      {/* ── Funnel steps ─────────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Как это работает" title="Воронка по шагам" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CRYPTO.steps.map((s) => (
            <Card key={s.n} className="flex flex-col p-5">
              <span
                className="text-[24px] font-bold tabular-nums leading-none"
                style={{ color: GOLD, letterSpacing: 'var(--tracking-tight)' }}
              >
                {s.n}
              </span>
              <div className="mt-2 text-[14px] font-semibold text-[var(--text)]">{s.title}</div>
              <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--text-muted)]">{s.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Partner income ───────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Сколько ты зарабатываешь" title="Сплит профита по лиду" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CRYPTO.income.map((t) => (
            <Card
              key={t.label}
              className={'flex flex-col p-5 ' + (t.hot ? 'ring-1 ring-[var(--warning)]' : '')}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                  {t.label}
                </span>
                {t.hot && <Badge tone="warning">Чаще всего</Badge>}
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-[11px] text-[var(--text-quiet)]">профит с лида платформе</span>
                <span className="text-[15px] font-semibold tabular-nums text-[var(--text-secondary)]">
                  {formatCents(t.profit)}
                </span>
              </div>

              <div className="mt-3 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--bg-base)]/40 px-4 py-3">
                <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                  Твоя доля · сплит 50/50
                </div>
                <div
                  className="mt-1 text-[28px] font-bold tabular-nums leading-none"
                  style={{ color: GOLD, letterSpacing: 'var(--tracking-tight)' }}
                >
                  {formatCents(Math.round(t.profit * 0.5))}
                </div>
              </div>

              <p className="mt-3 text-[12px] leading-relaxed text-[var(--text-muted)]">
                Профит платформы с торговли лида делится 50/50 — ваша половина начисляется автоматически.
              </p>
            </Card>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-[var(--text-quiet)]">
          Суммы профита — примеры для расчёта; фактическое начисление зависит от результата индивидуальной торговли лида.
        </p>
      </section>

      {/* ── Terms ────────────────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Условия" title="Что важно знать" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CRYPTO.terms.map((t) => (
            <Card key={t.label} className="flex flex-col p-5">
              <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                {t.label}
              </div>
              <div className="mt-1 text-[15px] font-semibold" style={{ color: GOLD }}>
                {t.value}
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-[var(--text-muted)]">{t.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      <StickyCta label={CRYPTO.name} selected={selected} onSelect={onSelect} pending={pending} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Rich detail — офферы с собственным лендингом (Vexa, Helix)
// ─────────────────────────────────────────────────────────────────────────

interface RichCard {
  title: string;
  meta?: string;
  desc: string;
}
interface RichOfferContent {
  name: string;
  tagline: string;
  pitch: string;
  revshare: number;
  color: string;
  siteUrl: string;
  tags: string[];
  stats: { value: string; label: string }[];
  cardsEyebrow: string;
  cardsTitle: string;
  cards: RichCard[];
  steps: { n: string; title: string; desc: string }[];
  terms: { value: string; label: string; desc: string }[];
}

/** Единое богатое оформление детали оффера: hero + витрина + воронка + условия. */
function RichOfferDetail({
  content: c,
  selected,
  onSelect,
  pending,
}: {
  content: RichOfferContent;
  selected: boolean;
  onSelect: () => void;
  pending: boolean;
}) {
  const C = c.color;
  return (
    <div className="mx-auto max-w-[1040px] pb-28">
      <BackLink />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <Card className="overflow-hidden" style={{ borderColor: `color-mix(in oklch, ${C} 24%, var(--border))` }}>
        <div
          className="p-7 md:p-9"
          style={{
            background: `radial-gradient(130% 150% at 0% 0%, color-mix(in oklch, ${C} 15%, var(--bg-raised)) 0%, var(--bg-raised) 60%)`,
          }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[var(--tracking-eyebrow)]"
              style={{
                backgroundColor: `color-mix(in oklch, ${C} 15%, transparent)`,
                color: C,
                border: `1px solid color-mix(in oklch, ${C} 30%, transparent)`,
              }}
            >
              <Percent size={11} strokeWidth={2.5} /> RevShare
            </span>
            {selected ? (
              <Badge tone="accent"><BadgeCheck size={11} strokeWidth={2.5} /> Выбран</Badge>
            ) : (
              <Badge tone="success" dot>Активен</Badge>
            )}
          </div>

          <h1 className="mt-4 text-[30px] md:text-[40px] font-bold leading-[1.05] tracking-tight text-[var(--text)]">
            {c.name}
          </h1>
          <p className="mt-1.5 text-[15px] font-medium" style={{ color: C }}>
            {c.tagline}
          </p>
          <p className="mt-4 max-w-[640px] text-[14px] leading-relaxed text-[var(--text-secondary)]">
            {c.pitch}
          </p>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {c.tags.map((t) => (
              <span
                key={t}
                className="rounded-[var(--radius-xs)] border border-[var(--border)] bg-[color-mix(in_oklch,var(--bg-base)_55%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-muted)]"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {c.stats.map((s) => (
              <div key={s.label}>
                <div
                  className="text-[22px] font-bold tabular-nums leading-none text-[var(--text)]"
                  style={{ letterSpacing: 'var(--tracking-tight)' }}
                >
                  {s.value}
                </div>
                <div className="mt-1 text-[11px] text-[var(--text-quiet)]">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-[var(--border-subtle)] flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                Твоя выплата
              </div>
              <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                <span
                  className="text-[40px] font-bold leading-none tabular-nums"
                  style={{ color: C, letterSpacing: 'var(--tracking-tight)' }}
                >
                  {c.revshare}%
                </span>
                <span className="text-[14px] font-medium text-[var(--text-muted)]">
                  RevShare с подтверждённой оплаты
                </span>
              </div>
            </div>
            <a href={c.siteUrl} target="_blank" rel="noreferrer" className="group shrink-0">
              <Button variant="secondary" size="sm">
                <Globe size={14} strokeWidth={2} />
                Сайт оффера
                <ArrowUpRight
                  size={14}
                  strokeWidth={2.5}
                  className="transition-transform duration-[var(--duration-base)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </Button>
            </a>
          </div>
        </div>
      </Card>

      {/* ── Витрина продукта ─────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow={c.cardsEyebrow} title={c.cardsTitle} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {c.cards.map((card) => (
            <Card key={card.title} className="flex flex-col p-5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[14px] font-semibold text-[var(--text)]">{card.title}</span>
                {card.meta && (
                  <span className="text-[12px] font-semibold tabular-nums" style={{ color: C }}>
                    {card.meta}
                  </span>
                )}
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-[var(--text-muted)]">{card.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Воронка ──────────────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Как ты зарабатываешь" title="Воронка по шагам" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {c.steps.map((s) => (
            <Card key={s.n} className="flex flex-col p-5">
              <span
                className="text-[24px] font-bold tabular-nums leading-none"
                style={{ color: C, letterSpacing: 'var(--tracking-tight)' }}
              >
                {s.n}
              </span>
              <div className="mt-2 text-[14px] font-semibold text-[var(--text)]">{s.title}</div>
              <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--text-muted)]">{s.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Условия ──────────────────────────────────────────────────── */}
      <section className="mt-8">
        <SectionHead eyebrow="Условия" title="Что важно знать" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {c.terms.map((t) => (
            <Card key={t.label} className="flex flex-col p-5">
              <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                {t.label}
              </div>
              <div className="mt-1 text-[15px] font-semibold" style={{ color: C }}>
                {t.value}
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-[var(--text-muted)]">{t.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      <StickyCta label={c.name} selected={selected} onSelect={onSelect} pending={pending} />
    </div>
  );
}

const REV_TERMS = (subject: string): RichOfferContent['terms'] => [
  { value: 'RevShare 50%', label: 'Модель', desc: `Половина подтверждённой суммы оплаты ${subject} — на ваш баланс. Фиксированного CPA нет.` },
  { value: 'По клику /r/…', label: 'Атрибуция', desc: 'Засчитывается лид, пришедший по вашей ссылке с вашим кодом.' },
  { value: 'Вручную', label: 'Подтверждение', desc: 'Конверсию по сделке подтверждает администратор после оплаты клиентом.' },
  { value: 'Честный трафик', label: 'Источники', desc: 'Без обмана и поддельных обещаний доходности — соблюдайте правила площадок.' },
];

const VEXA_CONTENT: RichOfferContent = {
  name: 'Vexa — AI-ассистенты для бизнеса',
  tagline: 'AI-ассистенты, которые отвечают за вас и продают лучше менеджеров',
  pitch:
    'Vexa внедряет чат-ботов на базе AI для сайтов, мессенджеров и CRM: консультации, квалификация лидов, поддержка первой линии, sales-ассистенты. Лид переходит по вашей партнёрской ссылке на сайт Vexa — клик и атрибуция закрепляются за вами.',
  revshare: 50,
  color: 'var(--accent-bright)',
  siteUrl: 'https://vexa-ai.duckdns.org',
  tags: ['AI-ассистенты', 'Чат-боты', 'CRM-интеграции', 'B2B'],
  stats: [
    { value: '50%', label: 'ваша доля RevShare' },
    { value: '47+', label: 'компаний у Vexa' },
    { value: '180 ч', label: 'экономии клиенту / мес' },
    { value: 'Сайт', label: 'точка входа лида' },
  ],
  cardsEyebrow: 'Что продаёт оффер',
  cardsTitle: '4 типа AI-ассистентов',
  cards: [
    { title: 'Консультант', desc: 'Отвечает о товарах, услугах, ценах и наличии — на сайте и в мессенджерах. −60% типовых обращений.' },
    { title: 'Квалификатор лидов', desc: 'Отсеивает нецелевых, передаёт «горячих» в CRM. Рост конверсии в продажу в 2–3 раза.' },
    { title: 'Поддержка 1-й линии', desc: 'Закрывает до 70% обращений без оператора, сложное эскалирует человеку.' },
    { title: 'Sales-ассистент', desc: 'Ведёт клиента по воронке, помогает выбрать и оформить заказ. Средний чек +15–25%.' },
  ],
  steps: [
    { n: '01', title: 'Переход по ссылке', desc: 'Лид кликает вашу партнёрскую ссылку /r/… — клик и ref-код закрепляются за вами.' },
    { n: '02', title: 'Сайт Vexa', desc: 'Лид знакомится с AI-ассистентами, кейсами и форматом работы студии.' },
    { n: '03', title: 'Заявка', desc: 'Оставляет заявку на расчёт или пишет в бота Vexa.' },
    { n: '04', title: 'Сделка → 50%', desc: 'Vexa внедряет ассистента; с подтверждённой оплаты половина начисляется вам.' },
  ],
  terms: REV_TERMS('приведённого вами клиента'),
};

const SCANLY_CONTENT: RichOfferContent = {
  name: 'Scanly — AI-сканер графиков',
  tagline: 'Кидаешь скрин графика в Telegram-бота — получаешь готовый сетап: паттерн, уровни, точку входа',
  pitch:
    'Scanly (@scanly_ai_bot) — AI-ассистент трейдера в Telegram. Юзер отправляет скриншот графика с любого таймфрейма (крипта, forex, акции) — бот за секунды распознаёт паттерны, размечает уровни поддержки/сопротивления и подсказывает точку входа, стоп и тейк. Лид переходит по вашей партнёрской ссылке /go/scanly?ref=<код> — клик логируется в affiliate_clicks, атрибуция закрепляется за вами на 30 дней.',
  revshare: 50,
  color: 'var(--success)',
  siteUrl: 'https://t.me/scanly_ai_bot',
  tags: ['AI-Трейдинг', 'Crypto + Forex', 'Telegram-бот', 'Smart Money', 'Технический анализ'],
  stats: [
    { value: '50%', label: 'ваша доля RevShare' },
    { value: '< 5 сек', label: 'на анализ графика' },
    { value: 'Любой ТФ', label: '1m → 1W' },
    { value: 'Telegram', label: 'точка входа лида' },
  ],
  cardsEyebrow: 'Что умеет бот',
  cardsTitle: 'Возможности Scanly',
  cards: [
    {
      title: 'Распознавание паттернов',
      meta: 'AI',
      desc: 'Голова и плечи, двойные вершины/дна, треугольники, флаги, клинья — бот находит классический ТА на чарте автоматически и подсвечивает их прямо на скриншоте.',
    },
    {
      title: 'Smart Money Concepts',
      meta: 'SMC',
      desc: 'Order Blocks, FVG, Liquidity Pools, Break of Structure, CHoCH — для трейдеров SMC/ICT-стиля. Бот размечает зоны интереса маркет-мейкеров.',
    },
    {
      title: 'Уровни и зоны',
      meta: 'S/R',
      desc: 'Сильные уровни поддержки и сопротивления, ключевые проторгованные зоны, динамические уровни (EMA/VWAP) — всё в одном изображении.',
    },
    {
      title: 'Точка входа · стоп · тейк',
      meta: 'Setup',
      desc: 'Бот предлагает конкретный сетап: где войти, куда поставить стоп-лосс, цели по тейк-профиту с риском/прибылью.',
    },
    {
      title: 'Multi-таймфрейм',
      meta: 'MTF',
      desc: 'Загружаешь скрины 1H / 4H / 1D — Scanly сводит их в один план: тренд старшего ТФ, точка входа на младшем.',
    },
    {
      title: 'Любой рынок',
      meta: 'Crypto · Forex · Stocks',
      desc: 'Работает с любыми источниками: TradingView, Binance, Bybit, MT4/5, скрины с акций. Распознаёт оси и инструмент автоматически.',
    },
  ],
  steps: [
    {
      n: '01',
      title: 'Переход по ссылке',
      desc: 'Лид кликает вашу партнёрскую ссылку https://nexus-partners.click/go/scanly?ref=<ваш_код> — наш сервер логирует клик в affiliate_clicks и редиректит на лендинг Scanly с пробросом ref.',
    },
    {
      n: '02',
      title: 'Telegram-бот',
      desc: 'С лендинга юзер открывает @scanly_ai_bot, кидает первый скрин графика — получает бесплатный пробный анализ.',
    },
    {
      n: '03',
      title: 'Подписка',
      desc: 'Чтобы продолжить пользоваться, оформляет подписку: разовая оплата или регулярная — внутри бота.',
    },
    {
      n: '04',
      title: 'Оплата → 50%',
      desc: 'Scanly подтверждает оплату; половина суммы автоматически начисляется на ваш баланс по модели RevShare. Видно в /balances.',
    },
  ],
  terms: REV_TERMS('приведённого вами трейдера'),
};

const HELIX_CONTENT: RichOfferContent = {
  name: 'Helix — Студия разработки полного цикла',
  tagline: 'Сайты, приложения и MVP — фикс-цена, фикс-срок, гарантия в договоре',
  pitch:
    'Helix — студия разработки полного цикла: лендинги, веб- и мобильные приложения, интернет-магазины, CRM и интеграции под ключ. Лид переходит по вашей партнёрской ссылке на сайт Helix — клик и атрибуция закрепляются за вами.',
  revshare: 50,
  color: 'var(--info)',
  siteUrl: 'https://helix-code.duckdns.org',
  tags: ['Веб-разработка', 'Мобильные приложения', 'MVP', 'Интеграции'],
  stats: [
    { value: '50%', label: 'ваша доля RevShare' },
    { value: '850+', label: 'клиентов у Helix' },
    { value: '6+ лет', label: 'студия на рынке' },
    { value: 'Сайт', label: 'точка входа лида' },
  ],
  cardsEyebrow: 'Что продаёт оффер',
  cardsTitle: '6 направлений разработки',
  cards: [
    { title: 'Сайты и лендинги', meta: 'от $200', desc: 'Под конкретную цель: заявка, звонок, продажа. Структура, тексты, скорость.' },
    { title: 'Мобильные приложения', meta: 'от $3 000', desc: 'iOS и Android — нативные или кроссплатформенные, стабильные и быстрые.' },
    { title: 'MVP для стартапа', meta: 'от $4 000', desc: 'Рабочий продукт с реальными пользователями за 8–12 недель — не макет.' },
    { title: 'Интернет-магазины', meta: 'от $600', desc: 'Каталог, оплата, доставка, личный кабинет, синхронизация с учётом.' },
    { title: 'CRM и автоматизация', meta: 'от $500', desc: 'Все заявки, клиенты и сделки в одном месте — с прозрачной воронкой.' },
    { title: 'Интеграции и доработки', meta: 'от $100', desc: 'Связываем сервисы между собой без ручного переноса данных.' },
  ],
  steps: [
    { n: '01', title: 'Переход по ссылке', desc: 'Лид кликает вашу партнёрскую ссылку /r/… — клик и ref-код закрепляются за вами.' },
    { n: '02', title: 'Сайт Helix', desc: 'Лид смотрит услуги и кейсы, прикидывает смету в калькуляторе.' },
    { n: '03', title: 'Заявка на расчёт', desc: 'Оставляет заявку или пишет в бота Helix.' },
    { n: '04', title: 'Заказ → 50%', desc: 'Helix делает проект; с подтверждённой оплаты половина начисляется вам.' },
  ],
  terms: REV_TERMS('приведённого вами заказчика'),
};

// ─────────────────────────────────────────────────────────────────────────
//  Generic (API-driven) detail
// ─────────────────────────────────────────────────────────────────────────

function GenericDetail({
  offer,
  selected,
  onSelect,
  pending,
}: {
  offer: Offer;
  selected: boolean;
  onSelect: () => void;
  pending: boolean;
}) {
  const meta = MODEL_META[offer.payoutModel] ?? MODEL_META.cpa;
  const { color, Icon } = meta;
  const creatives = Array.isArray(offer.creatives) ? offer.creatives : [];

  return (
    <div className="mx-auto max-w-[1040px] pb-28">
      <BackLink />

      {/* Hero */}
      <Card className="overflow-hidden">
        <div
          className="p-7 md:p-8"
          style={{
            background: `radial-gradient(130% 150% at 0% 0%, color-mix(in oklch, ${color} 14%, var(--bg-raised)) 0%, var(--bg-raised) 60%)`,
          }}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <span
              className="inline-flex h-12 w-12 items-center justify-center rounded-[var(--radius-md)]"
              style={{
                backgroundColor: `color-mix(in oklch, ${color} 16%, transparent)`,
                border: `1px solid color-mix(in oklch, ${color} 32%, transparent)`,
                color,
              }}
              aria-hidden
            >
              <Icon size={22} strokeWidth={2} />
            </span>
            {selected ? (
              <Badge tone="accent"><BadgeCheck size={11} strokeWidth={2.5} /> Выбран</Badge>
            ) : offer.isActive ? (
              <Badge tone="success" dot>Активен</Badge>
            ) : (
              <Badge tone="warning" dot>Пауза</Badge>
            )}
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold" style={{ color }}>
            {meta.label}
          </div>
          <h1 className="mt-1 text-[28px] md:text-[34px] font-bold leading-tight tracking-tight text-[var(--text)]">
            {offer.name || '—'}
          </h1>
          <p className="mt-4 max-w-[620px] text-[14px] leading-relaxed text-[var(--text-secondary)] whitespace-pre-wrap">
            {offer.description || 'Описание не задано'}
          </p>

          <div className="mt-6 pt-5 border-t border-[var(--border-subtle)] flex flex-wrap gap-x-10 gap-y-4">
            {offer.revsharePercent != null && offer.revsharePercent > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                  RevShare
                </div>
                <div className="mt-1 text-[28px] font-bold tabular-nums leading-none" style={{ color }}>
                  {offer.revsharePercent}%
                </div>
              </div>
            )}
            {offer.cpaCents != null && offer.cpaCents > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                  CPA за конверсию
                </div>
                <div className="mt-1 text-[28px] font-bold tabular-nums leading-none" style={{ color }}>
                  {formatCents(offer.cpaCents)}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Creatives */}
      <section className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-2">
                <ImageIcon size={15} className="text-[var(--accent-bright)]" />
                Креативы
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {creatives.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {creatives.map((c) => (
                  <a
                    key={c.id}
                    href={c.url || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2.5 rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-2.5 text-xs transition-colors hover:border-[var(--accent)]"
                  >
                    <Badge tone="neutral">{c.type || 'file'}</Badge>
                    <span className="flex-1 truncate text-[var(--text-muted)]">{c.url || ''}</span>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)]">Креативы пока не добавлены.</p>
            )}
          </CardContent>
        </Card>
      </section>

      <StickyCta label={offer.name || 'оффер'} selected={selected} onSelect={onSelect} pending={pending} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Sticky CTA
// ─────────────────────────────────────────────────────────────────────────

function StickyCta({
  label,
  selected,
  onSelect,
  pending,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  pending: boolean;
}) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-[var(--border)] bg-[var(--bg-base)]/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1040px] items-center justify-between gap-4 px-6 py-3">
        <div className="hidden text-sm text-[var(--text-muted)] sm:block">
          Готовы продвигать <span className="font-medium text-[var(--text)]">{label}</span>?
        </div>
        <Button
          onClick={onSelect}
          loading={pending}
          variant={selected ? 'secondary' : 'gradient'}
          size="lg"
          className="w-full sm:w-auto sm:min-w-[280px]"
        >
          {!pending && <Check size={16} />}
          {selected ? 'Оффер выбран' : 'Выбрать оффер'}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────

export function OfferDetailPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const isAiLab = slug === 'ai-lab';
  const isCrypto = slug === 'crypto-rev-50';
  const isVexa = slug === 'vexa-ai';
  const isHelix = slug === 'helix-code';
  const isScanly = slug === 'scanly';
  // Офферы с ручным богатым оформлением — API-запрос детали не нужен.
  const offer = partnerApi.offers.useGet(
    isAiLab || isCrypto || isVexa || isHelix || isScanly ? undefined : slug,
  );
  const select = partnerApi.offers.useSelect();
  const me = partnerApi.me.useGet();
  // Мультивыбор: набор активных офферов партнёра. Клик по уже выбранному — снимает выбор.
  const activeSlugs = me.data?.activeOfferSlugs ?? [];

  // Слаг оффера в БД: на страницах AI Lab / Crypto роут-параметр может быть
  // «псевдо-слагом» (ai-lab), а в БД оффер называется ai-rev-50 — выбираем по нему.
  const dbSlug = isAiLab ? 'ai-rev-50' : isCrypto ? 'crypto-rev-50' : slug;
  const selectOffer = async () => {
    try {
      const res = await select.mutateAsync(dbSlug);
      toast.success(res?.active ? 'Оффер добавлен в ваш набор' : 'Оффер убран из набора');
    } catch {
      /* toast handled globally */
    }
  };

  if (isAiLab) {
    return (
      <AiLabDetail
        selected={activeSlugs.includes('ai-rev-50')}
        onSelect={selectOffer}
        pending={select.isPending}
      />
    );
  }

  if (isCrypto) {
    return (
      <CryptoDetail
        selected={activeSlugs.includes('crypto-rev-50')}
        onSelect={selectOffer}
        pending={select.isPending}
      />
    );
  }

  if (isVexa) {
    return (
      <RichOfferDetail
        content={VEXA_CONTENT}
        selected={activeSlugs.includes('vexa-ai')}
        onSelect={selectOffer}
        pending={select.isPending}
      />
    );
  }

  if (isHelix) {
    return (
      <RichOfferDetail
        content={HELIX_CONTENT}
        selected={activeSlugs.includes('helix-code')}
        onSelect={selectOffer}
        pending={select.isPending}
      />
    );
  }

  if (isScanly) {
    return (
      <RichOfferDetail
        content={SCANLY_CONTENT}
        selected={activeSlugs.includes('scanly')}
        onSelect={selectOffer}
        pending={select.isPending}
      />
    );
  }

  if (offer.isLoading) {
    return (
      <div className="mx-auto max-w-[1040px]">
        <BackLink />
        <Card>
          <CardContent>
            <Skeleton className="h-7 w-1/3 mb-4" />
            <Skeleton className="h-3 w-full mb-2" />
            <Skeleton className="h-3 w-2/3" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!offer.data) {
    return (
      <div className="mx-auto max-w-[1040px]">
        <BackLink />
        <PageHeader title="Оффер не найден" subtitle={slug} />
        <Card>
          <CardContent>
            <p className="text-sm text-[var(--text-muted)]">
              Такого оффера нет или он больше не доступен. Вернитесь в каталог.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <GenericDetail
      offer={offer.data}
      selected={activeSlugs.includes(offer.data.slug)}
      onSelect={selectOffer}
      pending={select.isPending}
    />
  );
}
